// =====================================================================
// Assessment Center — Student Panel
// Layout shell for Dashboard / Available Exams / My Exams / Results.
// The Exam Page (exam-taking) intentionally does NOT use this layout —
// it's full-bleed, distraction-free, with its own chrome (see
// pages/ExamPage.tsx), matching the existing AviationHub exam-runner
// pattern (navy top bar, timer, bottom question-nav strip).
// =====================================================================
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "../../../shared/auth/AuthContext";

const navItems = [
  { to: "/student/assessment-center", label: "Dashboard", end: true, icon: "◧" },
  { to: "/student/assessment-center/available-exams", label: "Available Exams", icon: "▤" },
  { to: "/student/assessment-center/my-exams", label: "My Exams", icon: "▥" },
];

export default function StudentLayout() {
  const { user, signOut } = useAuth();
  const navigate = useNavigate();

  function handleSignOut() {
    signOut();
    navigate("/");
  }

  return (
    <div className="min-h-screen bg-navy-50/40 font-body text-slate-800">
      <div className="mx-auto flex max-w-[1400px]">
        <aside className="sticky top-0 hidden h-screen w-64 shrink-0 flex-col border-r border-border-soft bg-white lg:flex">
          <div className="flex items-center gap-2 border-b border-border-soft px-5 py-5">
            <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-navy-700 font-display text-base font-bold text-white">
              AH
            </div>
            <div>
              <p className="font-display text-sm font-semibold leading-tight text-navy-900">
                AviationHub
              </p>
              <p className="text-xs leading-tight text-slate-400">Assessment Center</p>
            </div>
          </div>

          <nav className="flex-1 space-y-1 px-3 py-4">
            {navItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                className={({ isActive }) =>
                  `flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors ${
                    isActive
                      ? "bg-navy-700 text-white"
                      : "text-slate-600 hover:bg-navy-50 hover:text-navy-800"
                  }`
                }
              >
                <span aria-hidden className="text-base">
                  {item.icon}
                </span>
                {item.label}
              </NavLink>
            ))}
          </nav>

          <div className="border-t border-border-soft px-5 py-4">
            <p className="text-xs text-slate-400">Student Panel</p>
            <p className="text-xs text-slate-400">Instructor pages live separately.</p>
          </div>
        </aside>

        <div className="min-h-screen flex-1">
          <header className="sticky top-0 z-30 flex items-center justify-between border-b border-border-soft bg-white/95 px-6 py-4 backdrop-blur">
            <div className="flex items-center gap-2 lg:hidden">
              <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-navy-700 font-display text-sm font-bold text-white">
                AH
              </div>
              <span className="font-display text-sm font-semibold text-navy-900">
                Assessment Center
              </span>
            </div>
            <div className="hidden lg:block" />
            <div className="flex items-center gap-3">
              <span className="hidden text-sm text-slate-500 sm:inline">{user?.fullName ?? "Student"}</span>
              <div
                className="flex h-9 w-9 items-center justify-center rounded-full bg-navy-100 font-display text-sm font-semibold text-navy-700"
                aria-hidden
              >
                ST
              </div>
              <button
                onClick={handleSignOut}
                className="rounded-lg border border-border-soft px-3 py-1.5 text-xs font-medium text-slate-500 hover:bg-navy-50 hover:text-navy-700"
              >
                Sign out
              </button>
            </div>
          </header>

          <nav className="flex gap-1 overflow-x-auto border-b border-border-soft bg-white px-4 py-2 lg:hidden">
            {navItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                className={({ isActive }) =>
                  `whitespace-nowrap rounded-lg px-3 py-1.5 text-sm font-medium ${
                    isActive ? "bg-navy-700 text-white" : "text-slate-600 hover:bg-navy-50"
                  }`
                }
              >
                {item.label}
              </NavLink>
            ))}
          </nav>

          <main className="px-4 py-6 sm:px-6 lg:px-8">
            <Outlet />
          </main>
        </div>
      </div>
    </div>
  );
}
